import React, { useRef } from 'react';

import type { TagEntity } from '@schibsted/frapi-schema';
import type {
  ByfunnFrapiSearchResponse,
  FrapiTeaserItem,
} from '../../../types/frapi-types.js';

import TeaserSearch from '../teasers/TeaserSearch.js';
import { LinkWithPulse } from '../../../../core/mandatory-integrations/pulse/components/LinkWithPulse.js';
import { useKeys } from '../../hooks/useKeys.js';
import { sectionMappings } from '../../layouts/front/sectionHelpers.js';
import { generateSearchQuery } from './helpers.js';

export interface SearchBarPreviewResultsProps {
  articles: FrapiTeaserItem[];
  searchTerm: string;
  debouncedSearchTerm: string;
  searchResult: ByfunnFrapiSearchResponse;
  isMobile: boolean;
  activeFilters: TagEntity[];
  section?: string;
}

export const SearchBarPreviewResults: React.FC<
  SearchBarPreviewResultsProps
> = ({
  articles,
  searchTerm,
  debouncedSearchTerm,
  searchResult,
  isMobile,
  section,
  activeFilters,
}) => {
  const linkRefs: React.MutableRefObject<HTMLAnchorElement[]> = useRef([]);
  const seeAllRef = useRef(null);
  const container = useRef<HTMLDivElement | null>(null);
  const { cursor } = useKeys(searchResult, seeAllRef, linkRefs, container);

  if (articles.length === 0 && searchTerm === debouncedSearchTerm) {
    return (
      <div className="search-results fail">
        <div>Hmm, ingen treff på dette søket🤔</div>
        <div className="fake-button">Vis alle treff</div>
      </div>
    );
  }
  const resultsLength = articles?.length;
  const hasResults = searchResult?.articles && searchTerm;
  const currentSection = section && sectionMappings[section]?.sectionId;

  const count = searchResult.resultsCount;
  const hasMany = isMobile ? count >= 5 : count >= 9;

  return (
    <div
      className={`search-results ${hasMany ? 'has-many' : ''}`}
      ref={container}
    >
      {articles.map((s, i) => (
        <TeaserSearch
          className={i === cursor ? 'active' : ''}
          {...s}
          mobile={isMobile}
          innerRef={(el: HTMLAnchorElement) => (linkRefs.current[i] = el)}
          key={s.id}
          pulseData={{
            placement: {
              containerName: 'search-results',
              positionInBundle: i + 1,
            },
          }}
        />
      ))}
      {articles.length > 0 && (
        <LinkWithPulse
          className={`see-more-search ${
            cursor === resultsLength && hasResults ? 'active' : ''
          }`}
          href={`/sok?phrase=${searchTerm}&size=${Math.min(count, 36)}${
            currentSection ? `&section=${section}` : ''
          }${generateSearchQuery(activeFilters, section)}`}
          objectId="search:see-more-results"
          name="See more results in search field"
          innerRef={seeAllRef}
        >
          {`Vis alle treff (${count})`}
        </LinkWithPulse>
      )}
    </div>
  );
};
